import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../../ui/Button';
import Icon from '../../../ui/Icon';
import DataTable from '../../../ui/DataTable/DataTable';
import Stat, { StatGrid } from '../../../ui/Stat/Stat';
import { ICONS } from '../../../ui/icon-map';
import { CellId, CellMuted, CellNumber, CellPlate, CellText } from '../../../ui/DataTable/cells';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { finesService, errorMessage } from '../../../services/finesService';
import { Notice } from '../../OperationsPortal/components/collectionsShared';
import { revenueBranchCode } from '../kpi.logic';
import { formatMoney } from '../../../constants/finance';
import { usePermissions } from '../../../hooks/usePermissions';
import { isInvygoContract } from '../../ContractsManagement/utils/contractRows';
import { INVYGO_RENT_NOTE } from '../../the-engine/customerDue.logic';

// Revenue by the owner's working rule (25 Sep 2026), from the engine, in two parts kept apart:
//  - clear profit = 1 AED on every Salik / Darb trip + the fine fee (5 % + its VAT) + 50 AED parking per airport
//    contract, less the Salik / Darb / parking of the branch's cars while on no contract;
//  - rent = the car's rent for the days on contract (an estimate until the real contract amounts are settled).
// the one money format (DUP-27): 2 decimals, en-AE grouping, AED first, empty -> —
const aed = (v) => formatMoney(Number(v) || 0);
const n2 = (v) => (Number(v) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const KIND = { toll: 'Salik', darb: 'Darb', parking: 'Parking' };

// An Invygo contract's rent is settled by the accountant with Invygo (owner, 28 Sep 2026): the page shows no rent
// figure for it - no rate, no rent, no add-ons, no Ejar money - only the note; its Salik, Darb and fine fees stay.
// The engine marks the row (revenue.py `invygo`) and never serves a report kept from before the mark
// (main._revenue_current); the web's own rule (isInvygoContract: any D04 number) is the row's second guard.
export const invygoRow = (c) => !!c && (c.invygo === true || isInvygoContract({ contractNo: c.contract }));
const INVYGO_CELL = <span className="ds-cell-empty" title={INVYGO_RENT_NOTE}>—</span>;
// the rent-side cells of a row: '—' with the note for Invygo, else the figure
const rentCell = (c, show) => (invygoRow(c) ? INVYGO_CELL : show());
const rentExport = (c, v) => (invygoRow(c) ? INVYGO_RENT_NOTE : v);

// The reports already seen in this session: moving between tabs shows them at once (the engine keeps them too,
// so even a new visit answers in a second; Refresh asks the engine to build the report again).
const seen = new Map();

const RevenueKPIs = ({ filters }) => {
    const code = revenueBranchCode(filters);
    // an unmapped branch or another city used to fall back to 'all' and show the whole company's revenue
    if (!code) {
        return (
            <Notice tone="warning">
                No revenue report for {filters.city && filters.city !== 'All' ? `${filters.city} / ` : ''}{filters.branch}: the revenue report covers
                Terminal 1, Terminal 2, Business Bay and Invygo (Dubai). Pick one of them, Airport or All.
            </Notice>
        );
    }
    return <RevenueReport code={code} filters={filters} />;
};

const RevenueReport = ({ code, filters }) => {
    // permissions phase 4 (4 Oct 2026): the Excel exports and the Refresh that rebuilds the report, each its own
    const { hasPermission } = usePermissions();
    const canExport = hasPermission('export_kpi');
    const canRebuild = hasPermission('kpi_rebuild');
    const key = `${code}|${filters.startDate}|${filters.endDate}`;
    const [state, setState] = useState(() => (seen.has(key)
        ? { loading: false, refreshing: false, error: null, data: seen.get(key) }
        : { loading: true, refreshing: false, error: null, data: null }));

    const load = useCallback((fresh, isAlive = () => true) => {
        setState((st) => (fresh && st.data ? { ...st, refreshing: true } : { loading: true, refreshing: false, error: null, data: null }));
        const ask = (again) => finesService.revenue({ branch: code, from: filters.startDate, to: filters.endDate, fresh: again });
        return ask(fresh)
            // a report without `invygo` was built before the Invygo rent rule: asked for once more, built now (review,
            // owner 28 Sep 2026 - its rent, per-day average and rate sources hold Invygo's made-up rent)
            .then((data) => (data && !data.invygo && !fresh ? ask(1) : data))
            .then((data) => {
                seen.set(key, data);
                if (isAlive()) setState({ loading: false, refreshing: false, error: null, data });
            })
            .catch((e) => isAlive() && setState((st) => ({ ...st, loading: false, refreshing: false, error: errorMessage(e) })));
    }, [code, filters.startDate, filters.endDate, key]);

    useEffect(() => {
        let alive = true;
        if (seen.has(key)) setState({ loading: false, refreshing: false, error: null, data: seen.get(key) });
        else load(false, () => alive);
        return () => { alive = false; };
    }, [key, load]);

    if (state.loading) return <LoadingSpinner message="Adding up rent, Salik, fines and parking for the period..." />;
    if (state.error && !state.data) return <Notice tone="warning">Revenue could not be loaded: {state.error}</Notice>;
    const { revenue: r, clearProfit: cp, rent, expenses: x, rows, expenseLines, rateSources, coverageMissing, builtAt } = state.data;
    const sources = Object.entries(rateSources || {}).map(([k, v]) => `${k} ${v}`).join(' · ');
    const rated = Object.values(rateSources || {}).reduce((t, v) => t + v, 0);
    const learned = Object.entries(rateSources || {}).filter(([k]) => k.startsWith('learned')).reduce((t, [, v]) => t + v, 0);
    const learnedPct = rated ? Math.round((learned / rated) * 100) : 0;
    const missing = Object.entries(coverageMissing || {});
    const invygoN = state.data.invygo ? Number(state.data.invygo.contracts) || 0 : rows.filter(invygoRow).length;
    // still no `invygo` after asking again: an engine without the Invygo rule. Its rent tiles (rent, add-ons, Ejar money,
    // the per-day average, the rate sources) hold Invygo's made-up rent, so none of them is shown - never a wrong figure
    // (taking Invygo out of two tiles left it in the others - review, owner 28 Sep 2026)
    const rentHidden = !state.data.invygo && invygoN > 0;

    const columns = [
        { key: 'contract', header: 'Contract', cell: (c) => <CellId>{c.contract}</CellId> },
        { key: 'customer', header: 'Customer', cell: (c) => <CellText>{c.customer || '—'}</CellText> },
        { key: 'branch', header: 'Branch', cell: (c) => <CellText>{c.branch}</CellText> },
        { key: 'plate', header: 'Car', cell: (c) => <CellPlate>{c.plate}</CellPlate> },
        { key: 'days', header: 'Days in period', cell: (c) => <CellNumber value={n2(c.days)} /> },
        {
            key: 'dailyRate', header: 'Rent per day', exportValue: (c) => rentExport(c, c.dailyRate),
            cell: (c) => (invygoRow(c) ? <CellMuted>{INVYGO_RENT_NOTE}</CellMuted> : (
                <span className="inline-flex flex-col items-center">
                    <CellNumber value={n2(c.dailyRate)} />
                    <CellMuted>{c.rateSource}</CellMuted>
                </span>
            )),
        },
        {
            key: 'tripFees', header: 'Salik / Darb fee', exportValue: (c) => c.tripFees,
            cell: (c) => (
                <span className="inline-flex flex-col items-center">
                    <CellNumber value={n2(c.tripFees)} />
                    {(c.salik > 0 || c.darb > 0) && <CellMuted>{`${c.salik} Salik · ${c.darb} Darb${c.unlimitedSalik ? ' · Unlimited Salik' : ''}`}</CellMuted>}
                </span>
            ),
        },
        { key: 'fineFees', header: 'Fine fee', exportValue: (c) => c.fineFees, cell: (c) => <CellNumber value={n2(c.fineFees)} /> },
        { key: 'airportParking', header: 'Airport parking', exportValue: (c) => c.airportParking, cell: (c) => <CellNumber value={n2(c.airportParking)} /> },
        {
            key: 'unlimitedMargin', header: 'Unlimited Salik', exportValue: (c) => c.unlimitedMargin,
            cell: (c) => (c.unlimitedPaid > 0 ? (
                <span className="inline-flex flex-col items-center">
                    <CellNumber value={n2(c.unlimitedMargin)} tone={c.unlimitedMargin >= 0 ? 'success' : 'danger'} />
                    <CellMuted>{`paid ${n2(c.unlimitedPaid)} · used ${n2(c.unlimitedUsed)}`}</CellMuted>
                </span>
            ) : <span className="ds-cell-empty">—</span>),
        },
        { key: 'profit', header: 'Clear profit', exportValue: (c) => c.profit, cell: (c) => <CellNumber value={n2(c.profit)} tone="success" /> },
        { key: 'rent', header: 'Rent (no VAT)', exportValue: (c) => rentExport(c, c.rent), cell: (c) => rentCell(c, () => <CellNumber value={n2(c.rent)} />) },
        { key: 'addons', header: 'Add-ons', exportValue: (c) => rentExport(c, c.addons), cell: (c) => rentCell(c, () => (c.addons ? <CellNumber value={n2(c.addons)} /> : <span className="ds-cell-empty">—</span>)) },
        { key: 'collected', header: 'Collected', exportValue: (c) => rentExport(c, c.collected), cell: (c) => rentCell(c, () => (c.collected == null ? <span className="ds-cell-empty">—</span> : <CellNumber value={n2(c.collected)} />)) },
        {
            key: 'outstanding', header: 'Outstanding', exportValue: (c) => rentExport(c, c.outstanding),
            sortValue: (c) => (c.outstanding == null || invygoRow(c) ? -Infinity : c.outstanding),
            cell: (c) => rentCell(c, () => (c.outstanding == null ? <span className="ds-cell-empty" title={c.outstandingNote || undefined}>{c.outstandingNote ? 'no figure' : '—'}</span>
                : <CellNumber value={n2(c.outstanding)} tone={c.outstanding > 0.5 ? 'danger' : undefined} />)),
        },
    ];
    const expenseColumns = [
        { key: 'kind', header: 'Type', accessor: (e) => KIND[e.kind] || e.kind, cell: (e) => <CellText>{KIND[e.kind] || e.kind}</CellText> },
        { key: 'when', header: 'When', cell: (e) => <span className="ds-cell-date">{e.when}</span> },
        { key: 'plate', header: 'Car', cell: (e) => <CellPlate>{e.plate}</CellPlate> },
        { key: 'place', header: 'Where', cell: (e) => <CellText>{e.place || '—'}</CellText> },
        { key: 'amount', header: 'Amount', exportValue: (e) => e.amount, cell: (e) => <CellNumber value={n2(e.amount)} tone="danger" /> },
    ];

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center justify-end gap-3 text-xs text-gray-600">
                {state.error && <span className="text-red-700">Refresh failed: {state.error}</span>}
                <span>Updated {builtAt ? String(builtAt).slice(0, 16) : '—'} · kept for an hour; this month and last month are rebuilt after every Ejar sync</span>
                {canRebuild && <Button size="sm" variant="secondary" onClick={() => load(1)} disabled={state.refreshing}
                    leadingIcon={<Icon glyph={ICONS.refresh} size="sm" className={state.refreshing ? 'animate-spin' : ''} />}>
                    {state.refreshing ? 'Refreshing…' : 'Refresh'}
                </Button>}
            </div>
            {missing.length > 0 && (
                <Notice tone="warning">
                    {missing.map(([k, days]) => `${KIND[k] || k}: no data for ${days.length} day(s)`).join(' · ')} - those trips are not counted yet.
                </Notice>
            )}

            <section className="flex flex-col gap-3">
                <div>
                    <h3 className="text-base font-bold text-gray-900">Clear profit</h3>
                    <p className="text-xs text-gray-600">
                        1 AED on every Salik / Darb trip + the 5% fine fee and its VAT + 50 AED parking on every contract opened at the airport
                        + what Unlimited Salik brought over the gates the customer really crossed, less the Salik, Darb and parking of the
                        branch's cars while on no contract.
                    </p>
                </div>
                <StatGrid min={180}>
                    <Stat label="Net clear profit" icon={ICONS.kpi} value={aed(cp.net)} tone={cp.net >= 0 ? 'success' : 'danger'} meta={`Fees ${aed(cp.fees)} less expenses ${aed(cp.expenses)}`} />
                    {/* only the trips that earn the 1 AED: Unlimited Salik contracts' trips earn none */}
                    <Stat label="Salik / Darb fee" icon={ICONS.salik || ICONS.chart} value={aed(cp.tripFees)}
                        meta={`${r.feeSalikTrips ?? r.salikTrips} Salik · ${r.feeDarbTrips ?? r.darbTrips} Darb trips x 1 AED`} />
                    <Stat label="Fine fee" icon={ICONS.fines || ICONS.warning} value={aed(cp.fineFees)} meta={`${r.fines} fines · 5% + VAT`} />
                    <Stat label="Airport parking" icon={ICONS.home} value={aed(cp.airportParking)} meta={`${r.airportContracts} airport contracts x 50 AED`} />
                    <Stat label="Unlimited Salik" icon={ICONS.salik || ICONS.chart} value={aed(cp.unlimitedMargin)} tone={cp.unlimitedMargin < 0 ? 'danger' : undefined}
                        meta={`${r.unlimited.contracts} contracts · paid ${aed(r.unlimited.paid)} less used ${aed(r.unlimited.used)}`} />
                    <Stat label="Expenses" icon={ICONS.calculator} value={aed(cp.expenses)} tone={cp.expenses > 0 ? 'danger' : undefined}
                        meta={`${x.toll.n} Salik · ${x.darb.n} Darb · ${x.parking.n} parking on no contract`} />
                </StatGrid>
            </section>

            <section className="flex flex-col gap-3">
                <div>
                    <h3 className="text-base font-bold text-gray-900">Rent and add-ons</h3>
                    <p className="text-xs text-gray-600">
                        The rent without VAT for the days on contract in the period, and the daily add-ons (insurance) as revenue. Per day: Ejar's
                        own contract calculation, else the rent learned from the opening comment, else the car's Ejar rate (500 or more is a month),
                        else the model price. Collected / outstanding: the whole contract, from Ejar's payments.
                    </p>
                </div>
                {rentHidden ? (
                    <Notice tone="warning">
                        Rent figures not shown: the engine built this report before Invygo's rent was taken out ({INVYGO_RENT_NOTE}), so its
                        rent totals still hold {invygoN} Invygo contract{invygoN === 1 ? '' : 's'}. Refresh once the engine is updated.
                    </Notice>
                ) : (
                <StatGrid min={180}>
                    <Stat label="Rent (no VAT)" icon={ICONS.car} value={aed(rent.total)}
                        meta={`${rows.length - invygoN} contracts${invygoN ? ` · ${invygoN} Invygo contract${invygoN === 1 ? '' : 's'}: ${INVYGO_RENT_NOTE} - not in this total` : ''}`} />
                    <Stat label="Add-ons (insurance)" icon={ICONS.wallet} value={aed(rent.addons)} meta="Charged per day on the contract" />
                    <Stat label="Collected" icon={ICONS.check} value={aed(rent.collected)} meta={`${rent.withEjar} contracts read from Ejar · total ${aed(rent.contractsTotal)} · deposits held ${aed(rent.deposits)} (not income)`} />
                    <Stat label="Outstanding" icon={ICONS.warning} value={aed(rent.outstanding)} tone={rent.outstanding > 0 ? 'danger' : undefined}
                        meta={`${rent.owing} contracts still owe${rent.noFigure ? ` · no figure for ${rent.noFigure} open contract(s) - Ejar could not price a return now` : ''}`} />
                    <Stat label="Days on contract" icon={ICONS.calendar} value={Math.round(rent.days).toLocaleString('en-US')}
                        meta={`AED ${n2(rent.perDay)} a day on average${rent.ratedDays != null && rent.ratedDays < rent.days ? ` (over the ${Math.round(rent.ratedDays).toLocaleString('en-US')} days with a rate)` : ''}`} />
                    <Stat label="Rent learned from Ejar" icon={ICONS.info} value={`${learnedPct}%`} meta={sources || 'No rent in the period'}
                        title="The rest is estimated from the car's Ejar rate or the model price" />
                </StatGrid>
                )}
            </section>

            <DataTable
                title="Revenue by contract"
                description="Every contract of the period: its clear profit and its rent, apart."
                columns={columns}
                data={rows}
                rowKey="contract"
                pageSize={50}
                searchPlaceholder="Search a contract, customer or car…"
                emptyIcon={ICONS.wallet}
                emptyTitle="No contracts in this period."
                exportFileName={canExport ? `revenue-${code}-${filters.startDate}-${filters.endDate}` : undefined}
            />

            <DataTable
                title="Expenses: trips on no contract"
                description="Salik, Darb and parking of the branch's cars when no customer had them - the company pays these."
                columns={expenseColumns}
                data={expenseLines}
                rowKey={(e, i) => `${e.kind}-${e.plate}-${e.when}-${i}`}
                pageSize={25}
                searchPlaceholder="Search a car or place…"
                emptyIcon={ICONS.check}
                emptyTitle="No trips on no contract in this period."
                exportFileName={canExport ? `expenses-${code}-${filters.startDate}-${filters.endDate}` : undefined}
            />
        </div>
    );
};

export default RevenueKPIs;
